import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, auditsAPI, findingsAPI, capaAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  Clock,
  TrendingUp,
  Building2,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

const COLORS = {
  primary: '#4A5690',
  secondary: '#E89A2E',
  success: '#10B981',
  danger: '#EF4444',
  warning: '#F59E0B',
  muted: '#64748B',
};

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [departmentScores, setDepartmentScores] = useState([]);
  const [recentAudits, setRecentAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, scoresRes, auditsRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getDepartmentScores(),
        auditsAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setDepartmentScores(scoresRes.data);
      setRecentAudits(auditsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const compliancePieData = stats ? [
    { name: 'Compliant', value: stats.compliance.compliant, color: COLORS.success },
    { name: 'Partially Compliant', value: stats.compliance.partially_compliant, color: COLORS.warning },
    { name: 'Non-Compliant', value: stats.compliance.non_compliant, color: COLORS.danger },
    { name: 'Pending', value: stats.compliance.pending, color: COLORS.muted },
  ].filter(d => d.value > 0) : [];

  const severityData = stats ? [
    { name: 'Critical', value: stats.severity.critical, fill: '#EF4444' },
    { name: 'High', value: stats.severity.high, fill: '#F97316' },
    { name: 'Medium', value: stats.severity.medium, fill: '#F59E0B' },
    { name: 'Low', value: stats.severity.low, fill: '#10B981' },
  ] : [];

  const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card className="metric-card" data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="metric-label">{title}</p>
          <p className="metric-value" style={{ color }}>{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
          <Icon size={24} style={{ color }} />
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="p-6">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-16" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const totalObservations = stats ? 
    stats.compliance.compliant + 
    stats.compliance.partially_compliant + 
    stats.compliance.non_compliant + 
    stats.compliance.not_applicable : 0;

  const complianceRate = totalObservations > 0 
    ? Math.round((stats.compliance.compliant / totalObservations) * 100) 
    : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome Banner */}
      <Card className="bg-gradient-to-r from-[#4A5690] to-[#3D477A] text-white border-0">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Welcome back, {user?.name?.split(' ')[0]}!
              </h2>
              <p className="text-white/80">
                Here's your ISO 27001 compliance overview
              </p>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold">{complianceRate}%</div>
                <div className="text-sm text-white/70">Compliance Rate</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Controls"
          value={stats?.total_controls || 0}
          icon={Shield}
          color={COLORS.primary}
          subtitle="ISO 27001:2022"
        />
        <MetricCard
          title="Compliant"
          value={stats?.compliance?.compliant || 0}
          icon={CheckCircle2}
          color={COLORS.success}
          subtitle="Controls meeting requirements"
        />
        <MetricCard
          title="Open Findings"
          value={stats?.findings?.open || 0}
          icon={AlertTriangle}
          color={COLORS.danger}
          subtitle={`${stats?.findings?.in_progress || 0} in progress`}
        />
        <MetricCard
          title="Overdue CAPA"
          value={stats?.capa?.overdue || 0}
          icon={Clock}
          color={COLORS.warning}
          subtitle={`${stats?.capa?.open || 0} total open`}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compliance Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Compliance Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {compliancePieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={compliancePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {compliancePieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-slate-500">
                No compliance data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Findings by Severity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Open Findings by Severity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={severityData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" width={80} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Department Compliance Scores */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Building2 size={20} className="text-[#4A5690]" />
            Department Compliance Scores
          </CardTitle>
        </CardHeader>
        <CardContent>
          {departmentScores.length > 0 ? (
            <div className="space-y-4">
              {departmentScores.map((dept) => (
                <div key={dept.department_id} className="flex items-center gap-4">
                  <div className="w-40 font-medium text-sm text-slate-700 truncate">
                    {dept.department_name}
                  </div>
                  <div className="flex-1">
                    <Progress 
                      value={dept.score} 
                      className="h-2"
                      style={{ 
                        '--progress-background': dept.score >= 80 ? COLORS.success : 
                          dept.score >= 50 ? COLORS.warning : COLORS.danger 
                      }}
                    />
                  </div>
                  <div className="w-16 text-right">
                    <span className={`font-semibold text-sm ${
                      dept.score >= 80 ? 'text-emerald-600' : 
                      dept.score >= 50 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {dept.score}%
                    </span>
                  </div>
                  <Badge variant="outline" className="w-20 justify-center">
                    {dept.compliant}/{dept.total_controls}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Building2 size={40} className="mx-auto mb-3 text-slate-300" />
              <p>No department scores available</p>
              <p className="text-sm">Start auditing to see compliance scores</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Audits */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <FileText size={20} className="text-[#4A5690]" />
            Recent Audits
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentAudits.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Audit Name</th>
                    <th>Period</th>
                    <th>Auditor</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAudits.map((audit) => (
                    <tr key={audit.id}>
                      <td className="font-medium text-slate-900">{audit.audit_name}</td>
                      <td>{audit.audit_period}</td>
                      <td>{audit.auditor_name || 'N/A'}</td>
                      <td>{new Date(audit.audit_date).toLocaleDateString()}</td>
                      <td>
                        <Badge className={
                          audit.status === 'Completed' ? 'badge-compliant' :
                          audit.status === 'In Progress' ? 'badge-partial' :
                          'badge-pending'
                        }>
                          {audit.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileText size={40} className="mx-auto mb-3 text-slate-300" />
              <p>No audits created yet</p>
              <p className="text-sm">Create your first audit to get started</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
